import { useAuth } from "@/context/AuthContext";
import { BookOpen, Printer } from "lucide-react";

const ROLE_MATRIX = [
  ["Dashboard, Operazioni, Mappa, Pipeline", "✓", "✓", "✓", "—"],
  ["Fonti & Ingestion (ricerca portali)", "✓", "✓", "—", "—"],
  ["Operatori (CRM anagrafica)", "✓", "—", "—", "—"],
  ["Data Room", "✓", "✓", "✓", "solo autorizzate"],
  ["Wallet & Due Diligence", "✓", "✓", "✓", "—"],
  ["Configurazione / Amministrazione", "✓", "—", "—", "—"],
];

const TOC = [
  ["intro", "1. Cos'è Rebreton"],
  ["ruoli", "2. Ruoli e permessi"],
  ["accesso", "3. Accesso e recupero password"],
  ["dashboard", "4. Dashboard"],
  ["operazioni", "5. Database operazioni e filtri"],
  ["dettaglio", "6. Dettaglio operazione"],
  ["requisiti", "7. Requisiti operazione"],
  ["mappa", "8. Mappa"],
  ["pipeline", "9. Pipeline"],
  ["ingestion", "10. Fonti & Ingestion"],
  ["crm", "11. Operatori & CRM"],
  ["dataroom", "12. Data Room"],
  ["wallet", "13. Wallet, pagamenti e Due Diligence"],
  ["admin", "14. Amministrazione"],
  ["beta", "15. Note ambiente beta"],
];

function H({ id, children }) {
  return <h2 id={id} className="text-xl font-medium text-white mt-10 mb-3 scroll-mt-24 pb-2 border-b border-white/10">{children}</h2>;
}
function P({ children }) { return <p className="text-sm text-zinc-300 leading-relaxed mb-3">{children}</p>; }
function LI({ children }) { return <li className="text-sm text-zinc-300 leading-relaxed">{children}</li>; }
function B({ children }) { return <strong className="text-white font-medium">{children}</strong>; }

export default function Manuale() {
  const { user } = useAuth();

  return (
    <div className="max-w-4xl mx-auto" data-testid="manuale-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 no-print">
        <div>
          <h1 className="text-2xl sm:text-3xl font-light text-white flex items-center gap-3"><BookOpen className="w-7 h-7 text-[#EA7B2B] shrink-0" /> Manuale dell'applicativo</h1>
          <p className="text-sm text-zinc-500 mt-1">Guida operativa a Rebreton · Beta Aste — origination engine per operazioni immobiliari distressed</p>
        </div>
        <button onClick={() => window.print()} data-testid="print-manual-btn" className="self-start sm:self-auto shrink-0 flex items-center gap-2 bg-[#EA7B2B] text-black text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-[#F59E4A] transition-colors">
          <Printer className="w-4 h-4" /> Stampa / Scarica PDF
        </button>
      </div>

      {/* Indice */}
      <div className="bg-[#122A3F] border border-white/10 rounded-sm p-5 mb-8 no-print" data-testid="manuale-toc">
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Indice</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {TOC.map(([id, label]) => (
            <a key={id} href={`#${id}`} className="text-sm text-zinc-300 hover:text-[#EA7B2B] transition-colors">{label}</a>
          ))}
        </div>
      </div>

      <div className="manual-content bg-[#122A3F] border border-white/10 rounded-sm px-6 sm:px-10 py-8">
        <div className="print-title hidden">
          <h1 className="text-2xl font-semibold text-white">Rebreton · Beta Aste — Manuale dell'applicativo</h1>
        </div>

        <H id="intro">1. Cos'è Rebreton</H>
        <P>Rebreton è un <B>origination engine</B> per operazioni immobiliari distressed (aste giudiziarie, saldo e stralcio, NPL/UTP, acquisto diretto). Non è un semplice aggregatore: <B>trova, legge, calcola, qualifica e prioritizza</B> le operazioni in automatico.</P>
        <P>Il sistema acquisisce annunci dai portali pubblici, scarica e analizza i documenti (perizia, avviso, ordinanza) con l'intelligenza artificiale, calcola indicatori economici e assegna a ogni operazione uno <B>Score di investimento</B>, un <B>Confidence Score</B> (affidabilità dei dati) e una <B>priorità</B>.</P>

        <H id="ruoli">2. Ruoli e permessi</H>
        <P>L'accesso alle funzioni dipende dal ruolo dell'utente:</P>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI><B>Amministratore</B>: accesso completo, configurazione, integrazioni, audit, pricing, ingestion.</LI>
          <LI><B>Analista / Deal Originator</B>: gestione operazioni, ingestion, upload perizie, avanzamento workflow.</LI>
          <LI><B>Responsabile Investimenti</B>: monitoraggio pipeline, KPI, Data Room, Wallet.</LI>
          <LI><B>Investitore</B>: sola lettura, accesso limitato alla Data Room sulle operazioni autorizzate (senza note interne né metriche riservate).</LI>
        </ul>
        <div className="overflow-x-auto border border-white/10 rounded-sm">
          <table className="w-full text-sm">
            <thead className="bg-[#0A1826]/80 text-xs text-zinc-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Funzione</th>
                <th className="px-3 py-2 font-medium">Admin</th>
                <th className="px-3 py-2 font-medium">Analista</th>
                <th className="px-3 py-2 font-medium">Responsabile</th>
                <th className="px-3 py-2 font-medium">Investitore</th>
              </tr>
            </thead>
            <tbody>
              {ROLE_MATRIX.map((r, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-3 py-2 text-zinc-300">{r[0]}</td>
                  {r.slice(1).map((v, j) => <td key={j} className="px-3 py-2 text-center text-zinc-400">{v}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <H id="accesso">3. Accesso e recupero password</H>
        <P><B>Login:</B> inserisci l'<B>indirizzo email completo</B> (es. <span className="font-mono text-[#EA7B2B]">nome@dominio.it</span>) e la password. Attenzione: non basta il nome utente, serve l'email intera.</P>
        <P><B>Password dimenticata:</B> dalla pagina di login clicca su “Password dimenticata?”, inserisci l'email e riceverai un link di reset (valido 60 minuti) per impostare una nuova password.</P>
        <P className="text-zinc-400"><B>Nota beta:</B> l'invio email si attiva quando viene configurata la chiave del servizio email. Fino ad allora, richiedi all'amministratore l'assistenza per il reset.</P>
        <P><B>Cambio password:</B> puoi cambiarla in qualsiasi momento; l'amministratore condiviso può essere obbligato a cambiarla al primo accesso.</P>

        <H id="dashboard">4. Dashboard</H>
        <P>La <B>Dashboard</B> (Centro di comando) mostra la sintesi esecutiva: operazioni analizzate e qualificate, pipeline potenziale (€), valore perizia e valore base aggregati, ROI medio, Confidence e Completezza medi, nuove operazioni e variazioni nelle ultime 24h, distribuzione per priorità, per stato del workflow e per strategia.</P>

        <H id="operazioni">5. Database operazioni e filtri</H>
        <P>La sezione <B>Operazioni</B> elenca tutte le operazioni con Score, Confidence, priorità, immobile, procedura, strategia, prezzo base, valore, ROI, occupazione, stato ed esito di qualifica.</P>
        <P>Filtri disponibili:</P>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI><B>Ricerca libera</B>: RGE, comune, indirizzo, foglio, particella, custode, delegato, tribunale.</LI>
          <LI><B>Zona</B>: comune o provincia (riconosce sia il nome sia la sigla, es. “Roma” o “RM”).</LI>
          <LI><B>Raggio</B>: entro X km dalla zona indicata (10/25/50/100 km).</LI>
          <LI><B>Priorità, comune, strategia, fonte, qualificazione</B> e <B>ordinamento</B> (score, priorità, ROI, recenti).</LI>
          <LI><B>Data di acquisizione</B> (Dal / Al) e <B>Run di ingestion</B> (per vedere le operazioni di una singola esecuzione).</LI>
          <LI><B>Contatori per fonte</B>: chip cliccabili che filtrano rapidamente per portale di provenienza.</LI>
        </ul>
        <P><B>Export:</B> pulsanti <B>Excel</B> e <B>CSV</B> esportano l'elenco filtrato. <B>Analizza perizia PDF</B> (admin/analista) permette di caricare una perizia e farla analizzare dall'AI.</P>

        <H id="dettaglio">6. Dettaglio operazione</H>
        <P>Cliccando su un'operazione si apre la scheda completa con:</P>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI><B>Score investimento</B> (0-100), <B>Confidence</B> (affidabilità dati) e <B>Completezza</B>, <B>Priorità</B> automatica.</LI>
          <LI><B>Esito di qualifica</B> (Qualificata/Scartata) con i criteri non rispettati.</LI>
          <LI><B>Requisiti operazione</B> (vedi capitolo 7).</LI>
          <LI><B>Immobile, posizione debitoria, procedura</B> con indicatore di <B>affidabilità dato</B> (Certo / Dedotto / Da verificare).</LI>
          <LI><B>Verifica incrociata fonti</B> (Portale vs Documenti) e <B>Confronto tra portali</B>.</LI>
          <LI><B>Scoring Engine</B> (8 criteri, radar), <B>analisi economico-finanziaria</B>, <B>report AI</B>.</LI>
          <LI><B>Valutazione esterna</B> e <B>indici di zona</B> (attivi con i servizi OpenAPI).</LI>
          <LI><B>CRM</B> (assegnazione operatore, prossimo contatto, attività, note), <B>contatti pubblici</B> con compliance, <B>documenti</B> e <B>Due Diligence</B>.</LI>
        </ul>

        <H id="requisiti">7. Requisiti operazione</H>
        <P>In evidenza nella scheda, il pannello <B>Requisiti operazione</B> verifica 5 condizioni <B>lette dalla perizia</B>, ciascuna con esito <span className="text-[#10B981]">OK</span> / <span className="text-[#E11D48]">NON SODDISFATTO</span> / <span className="text-[#D97706]">DA VERIFICARE</span> (dato non presente nei documenti):</P>
        <ol className="list-decimal pl-5 space-y-1 mb-3">
          <LI><B>Piena proprietà dell'esecutato</B>.</LI>
          <LI><B>Assenza di abusi</B> (o, se presenti, costi di regolarizzazione <B>≤ 20% del valore di perizia</B>).</LI>
          <LI><B>Nessuna causa in corso</B>.</LI>
          <LI><B>Immobile occupato dall'esecutato</B>.</LI>
          <LI><B>Spese condominiali note</B>.</LI>
        </ol>
        <P>Quando un dato non è presente nella perizia il requisito è segnato “DA VERIFICARE”: caricando una perizia completa i valori si popolano automaticamente dall'analisi AI.</P>

        <H id="mappa">8. Mappa</H>
        <P>La <B>Mappa</B> geolocalizza le operazioni con marker colorati per priorità. Filtri per Zona, raggio km, fonte, score, ROI, stato, tipologia, tribunale. Utile per valutare la concentrazione geografica delle occasioni.</P>

        <H id="pipeline">9. Pipeline</H>
        <P>La <B>Pipeline</B> è una board Kanban con gli stati del workflow (da “Nuova” a “Acquistata/Scartata”). Ogni operazione può essere avanzata di stato dalla scheda di dettaglio, con motivazione e cronologia.</P>

        <H id="ingestion">10. Fonti & Ingestion</H>
        <P>La sezione <B>Fonti & Ingestion</B> (admin/analista) acquisisce le operazioni dai portali pubblici (Aste Giudiziarie, Astalegale).</P>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI><B>Ricerca mirata</B>: scrivi uno o più comuni/province nel campo “Comuni/province” e premi <B>Cerca nuove operazioni</B> per cercare solo lì.</LI>
          <LI><B>Zone preferite</B> (admin): salva un elenco di zone usate automaticamente ad ogni ricerca schedulata.</LI>
          <LI><B>Esecuzione automatica</B> (scheduler): attivabile ogni N ore, con numero massimo di operazioni per ciclo.</LI>
          <LI><B>Storico esecuzioni</B> e log in tempo reale (annunci trovati, create, arricchite, già presenti, errori).</LI>
        </ul>
        <P className="text-zinc-400"><B>Nota:</B> la ricerca è best-effort sugli annunci pubblici disponibili al momento. Per comuni piccoli può non restituire risultati subito; le zone preferite + lo scheduler li acquisiscono man mano che vengono pubblicati.</P>

        <H id="crm">11. Operatori & CRM</H>
        <P>Gli <B>Operatori</B> (anagrafica gestita dall'admin) possono essere assegnati alle operazioni. Su ogni operazione il CRM consente: assegnazione operatore, data del prossimo contatto, stato trattativa, attività/to-do e note condivise. I promemoria compaiono nella campanella in alto.</P>

        <H id="dataroom">12. Data Room</H>
        <P>La <B>Data Room</B> raccoglie le operazioni qualificate con documenti condivisi, attività assegnabili, note interne e gestione degli utenti autorizzati. Gli <B>investitori</B> vedono in sola lettura solo le operazioni autorizzate e solo i documenti contrassegnati come visibili, senza note interne né metriche riservate.</P>

        <H id="wallet">13. Wallet, pagamenti e Due Diligence</H>
        <P>Il <B>Wallet</B> è un credito prepagato a livello di organizzazione, usato per i servizi di <B>Due Diligence</B> a richiesta (visura catastale ordinaria/storica, ispezione ipotecaria, visure camerali, ecc.).</P>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI><B>Ricarica</B> tramite Stripe (in beta: modalità test).</LI>
          <LI><B>Due Diligence</B>: dalla scheda operazione scegli il servizio, vedi anteprima costo/fonte/tempi, confermi e il costo viene addebitato al Wallet; il documento risultante è scaricabile.</LI>
          <LI><B>Limiti di spesa</B> mensili per utente (impostati dall'admin) e movimenti tracciati.</LI>
        </ul>

        <H id="admin">14. Amministrazione</H>
        <P>Riservata all'<B>amministratore</B>:</P>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI><B>Integrazioni</B>: chiavi e configurazione dei servizi esterni (OpenAPI per valutazioni e visure).</LI>
          <LI><B>Audit & GDPR</B>: registro delle azioni (chi, quando, cosa, valore prima/dopo).</LI>
          <LI><B>Configurazione</B>: pesi di scoring, criteri, soglie e assunzioni economiche, con ricalcolo massivo.</LI>
          <LI><B>Pricing e ricariche</B>: markup dei servizi, prezzi minimi, tagli di ricarica; dashboard economica (ricavi, margine, MRR, ARPU).</LI>
          <LI><B>Dati demo</B>: conteggio ed eliminazione dei dati dimostrativi.</LI>
        </ul>

        <H id="beta">15. Note ambiente beta</H>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI>I <B>pagamenti Stripe</B> sono in modalità <B>test</B> (nessun addebito reale).</LI>
          <LI>I <B>servizi OpenAPI</B> (valutazioni €/mq, visure catastali/ipotecarie) sono attivi solo dopo l'inserimento di una chiave API valida.</LI>
          <LI>L'<B>invio email</B> (recupero password, notifiche) si attiva quando viene configurata la chiave del servizio email.</LI>
          <LI>Le operazioni contrassegnate <B>DEMO</B> non generano chiamate reali né addebiti.</LI>
        </ul>
        <P className="text-zinc-400">Per assistenza o segnalazioni durante il beta, contatta l'amministratore del tuo team.</P>

        <div className="mt-10 pt-4 border-t border-white/10 text-xs text-zinc-600">
          Rebreton · Beta Aste — Manuale generato per l'ambiente di beta test.{user?.name ? ` Utente: ${user.name} (${user.role_label || user.role}).` : ""}
        </div>
      </div>
    </div>
  );
}
